import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Breadcrumb from '../components/Breadcrumb';
import { ArrowLeft, Download, Eye, Tag, Clock, ChevronDown, ChevronUp } from 'lucide-react';

const LibraryDocument = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const [document, setDocument] = useState(null);
  const [versions, setVersions] = useState([]);
  const [files, setFiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAllVersions, setShowAllVersions] = useState(false);

  useEffect(() => {
    loadDocument();
  }, [documentId]);

  const loadDocument = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Doküman detayı
      const docResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/library/documents/${documentId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (docResponse.ok) {
        const docData = await docResponse.json();
        setDocument(docData);
        
        // Versiyonları yükle
        const versionsResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/library/documents/${documentId}/versions`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        
        if (versionsResponse.ok) {
          const versionsData = await versionsResponse.json();
          setVersions(versionsData);
          
          // Her versiyon için dosyaları yükle
          for (const version of versionsData) {
            loadVersionFiles(version.id);
          }
        }
      } else {
        alert('Doküman yüklenemedi');
        navigate('/library');
      }
    } catch (error) {
      console.error('Doküman yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadVersionFiles = async (versionId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/library/documents/${documentId}/versions/${versionId}/files`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const filesData = await response.json();
        setFiles(prev => ({ ...prev, [versionId]: filesData }));
      }
    } catch (error) {
      console.error('Dosyalar yüklenemedi:', error);
    }
  };

  const handleDownload = async (file) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/library/files/${file.id}/download`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        window.open(data.download_url, '_blank');
      } else {
        alert('Dosya indirilemedi');
      }
    } catch (error) {
      console.error('İndirme hatası:', error);
      // Fallback to direct URL
      window.open(file.url, '_blank');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!document) return null;

  const publishedVersion = versions.find(v => v.isPublishedVersion);
  const publishedFiles = publishedVersion ? files[publishedVersion.id] || [] : [];

  return (
    <div>
      <Breadcrumb 
        items={[
          { label: 'Doküman Kütüphanesi', path: '/library' },
          { label: document.title, path: null }
        ]} 
      />

      <button
        onClick={() => navigate('/library')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft size={20} />
        Geri
      </button>

      {/* Doküman Başlığı */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="text-4xl">{document.category === 'Plan Örneği' ? '📘' : document.category === 'Etkinlik' ? '🎨' : '📄'}</div>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 mb-3">{document.title}</h1>
            
            <div className="flex flex-wrap gap-3 mb-4">
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                📂 {document.category}
              </span>
              {document.targetAudience.map((audience, idx) => (
                <span key={idx} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                  👥 {audience}
                </span>
              ))}
            </div>
            
            {document.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {document.tags.map((tag, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm">
                    <Tag size={14} />
                    {tag}
                  </span>
                ))}
              </div>
            )}
            
            <p className="text-gray-600 leading-relaxed">{document.description}</p>
          </div>
        </div>
      </div>

      {/* Son Yayınlanan Versiyon */}
      {publishedVersion && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="text-lg font-bold text-gray-900">
              Son Yayınlanan Versiyon: v{publishedVersion.versionNumber}
            </div>
            <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm">Aktif</span>
          </div>
          
          {publishedVersion.changeLog && (
            <p className="text-gray-600 mb-4 italic">"{publishedVersion.changeLog}"</p>
          )}
          
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900">📄 Dosyalar:</h3>
            {publishedFiles.map((file) => (
              <div key={file.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="text-2xl">
                    {file.fileType === 'PDF' ? '📄' : file.fileType === 'Word' ? '📝' : file.fileType === 'PowerPoint' ? '📊' : '📎'}
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{file.fileName}</div>
                    <div className="text-sm text-gray-500">
                      {file.fileType} • {formatFileSize(file.fileSize)}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  {file.allowPreview && (
                    <button className="p-2 text-blue-600 hover:bg-blue-50 rounded">
                      <Eye size={20} />
                    </button>
                  )}
                  {file.allowDownload && (
                    <button 
                      onClick={() => handleDownload(file)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                    >
                      <Download size={18} />
                      İndir
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Geçmiş Versiyonlar */}
      {versions.length > 1 && (
        <div className="bg-white rounded-lg shadow p-6">
          <button
            onClick={() => setShowAllVersions(!showAllVersions)}
            className="w-full flex items-center justify-between text-left"
          >
            <h3 className="text-lg font-semibold text-gray-900">
              📚 Geçmiş Versiyonlar ({versions.length - 1})
            </h3>
            {showAllVersions ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
          
          {showAllVersions && (
            <div className="mt-4 space-y-3">
              {versions.filter(v => !v.isPublishedVersion).map((version) => (
                <div key={version.id} className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">v{version.versionNumber}</span>
                    <span className="text-sm text-gray-500">
                      <Clock size={14} className="inline mr-1" />
                      {new Date(version.createdAt).toLocaleDateString('tr-TR')}
                    </span>
                  </div>
                  {version.changeLog && (
                    <p className="text-sm text-gray-600 italic">"{version.changeLog}"</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LibraryDocument;
